import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { FormCheckboxType } from '#pie/components/form/types';
import type { CreateDataRequestDto } from '#pie/internal/api/pie-bff';
import { AddressLevel, FileFormat, Period } from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';

const { ConditionalField, FormCheckbox, FormText } =
  typedFormFields<CreateDataRequestDto>();

export const Periods = () => {
  // Over welke periode wil je data ontvangen?
  const { t } = useTranslation('dataRequest');
  const currentYear = new Date().getFullYear();
  const dynamicYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const options: FormCheckboxType[] = [
    {
      label: t(`periods.${Period.Past5Years}`),
      value: Period.Past5Years,
    },
    ...dynamicYears.map((year) => {
      const periodKey = `_${year}` as keyof typeof Period;
      return {
        label: t('periods.year', { year: year }),
        value: Period[periodKey],
      };
    }),
    {
      label: t(`periods.${Period.Other}`),
      renderInput: (
        <ConditionalField
          field="periodNote"
          condition={(fieldValue) =>
            !!fieldValue('periods') &&
            fieldValue('periods')!.includes(Period.Other)
          }
        >
          <FormText
            fullWidth
            name="periodNote"
            showWordCount
            rules={{ maxLength: 25, minLength: 2, required: true }}
          />
        </ConditionalField>
      ),
      value: Period.Other,
    },
  ];

  const { watch, setValue } = useFormContext<CreateDataRequestDto>();
  const gridOperatorData = watch('periods');
  const condition = () => {
    const fileFormats = watch('fileFormats');
    if (
      fileFormats &&
      fileFormats.length > 0 &&
      !fileFormats.includes(FileFormat.Other)
    )
      return true;
    if (watch('fileFormatNote')) return true;
    if (watch('addressLevel') === AddressLevel.Cluster) return true;
    if (watch('customerMandateFile')) return true;
    return false;
  };

  useEffect(() => {
    if (!condition() || !gridOperatorData || gridOperatorData.length <= 1)
      return;

    const multiYearPeriods = [Period.Past5Years, Period.Other] as Period[];
    const lastSelectedPeriod = gridOperatorData.at(-1);

    if (lastSelectedPeriod && multiYearPeriods.includes(lastSelectedPeriod)) {
      setValue('periods', [lastSelectedPeriod]);
    } else if (
      gridOperatorData?.some((period) => multiYearPeriods.includes(period)) &&
      gridOperatorData.length > 1
    ) {
      const filtered = gridOperatorData.filter(
        (value) => value !== Period.Past5Years && value !== Period.Other,
      );
      setValue('periods', filtered);
    }
  }, [gridOperatorData, setValue]);

  return (
    <ConditionalField
      field="periods"
      condition={(formValue) => {
        const fileFormats = formValue('fileFormats');
        if (
          fileFormats &&
          fileFormats.length > 0 &&
          !fileFormats.includes(FileFormat.Other)
        )
          return true;
        if (formValue('fileFormatNote')) return true;
        if (formValue('addressLevel') === AddressLevel.Cluster) return true;
        if (formValue('customerMandateFile')) return true;
        return false;
      }}
    >
      <FormCheckbox
        infoTooltip={t('periods.tooltip')}
        label={t('periods')}
        name="periods"
        options={options}
        rules={{ required: true }}
      />
    </ConditionalField>
  );
};
